import { useState, useEffect } from 'react';
import { Plus, Trash2, CheckCircle, Circle, Clock, X, Flag } from '../../icons/wp-icons';
import { getMeilensteine, createMeilenstein, updateMeilenstein, deleteMeilenstein } from '../../api/projektApi';
import toast from 'react-hot-toast';

const STATUS_CONFIG = {
  OFFEN: { label: 'Offen', color: 'text-gray-500', bgColor: 'bg-gray-100', icon: Circle },
  IN_ARBEIT: { label: 'In Arbeit', color: 'text-blue-500', bgColor: 'bg-blue-100', icon: Clock },
  ERLEDIGT: { label: 'Erledigt', color: 'text-green-500', bgColor: 'bg-green-100', icon: CheckCircle }
};

export default function ProjektMeilensteine({ projektId }) {
  const [meilensteine, setMeilensteine] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [formData, setFormData] = useState({
    titel: '',
    beschreibung: '',
    faelligAm: new Date().toISOString().split('T')[0]
  });

  useEffect(() => {
    loadMeilensteine();
  }, [projektId]);

  const loadMeilensteine = async () => {
    try {
      const data = await getMeilensteine(projektId);
      setMeilensteine(data);
    } catch (error) {
      toast.error('Fehler beim Laden');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    try {
      await createMeilenstein(projektId, formData);
      toast.success('Meilenstein erstellt');
      setShowAdd(false);
      setFormData({ titel: '', beschreibung: '', faelligAm: new Date().toISOString().split('T')[0] });
      loadMeilensteine();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Fehler beim Erstellen');
    }
  };

  const handleStatusChange = async (meilenstein, newStatus) => {
    try {
      await updateMeilenstein(projektId, meilenstein.id, { status: newStatus });
      loadMeilensteine();
    } catch (error) {
      toast.error('Fehler beim Aktualisieren');
    }
  };

  const handleDelete = async (meilensteinId) => {
    if (!confirm('Meilenstein wirklich löschen?')) return;
    try {
      await deleteMeilenstein(projektId, meilensteinId);
      toast.success('Meilenstein gelöscht');
      loadMeilensteine();
    } catch (error) {
      toast.error('Fehler beim Löschen');
    }
  };

  if (loading) {
    return (
      <div className="bg-surface rounded-card shadow-card p-6">
        <div className="flex justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  const fortschritt = meilensteine.length > 0
    ? Math.round((meilensteine.filter(m => m.status === 'ERLEDIGT').length / meilensteine.length) * 100)
    : 0;

  return (
    <div className="bg-surface rounded-card shadow-card p-6">
      <div className="flex justify-between items-center mb-4">
        <div>
          <h3 className="font-semibold text-text flex items-center gap-2">
            <Flag className="w-5 h-5 text-text-muted" />
            Meilensteine ({meilensteine.length})
          </h3>
          <div className="flex items-center gap-2 mt-2">
            <div className="w-32 h-2 bg-gray-200 rounded-full overflow-hidden">
              <div
                className="h-full bg-green-500 transition-all duration-300"
                style={{ width: `${fortschritt}%` }}
              />
            </div>
            <span className="text-sm text-text-muted">{fortschritt}% abgeschlossen</span>
          </div>
        </div>
        <button
          onClick={() => setShowAdd(true)}
          className="flex items-center gap-2 px-3 py-2 bg-primary text-white rounded-button hover:bg-primary-dark transition text-sm"
        >
          <Plus className="w-4 h-4" />
          Meilenstein hinzufügen
        </button>
      </div>

      {showAdd && (
        <div className="bg-surface-elevated rounded-card p-4 mb-4">
          <div className="flex justify-between items-center mb-4">
            <h4 className="font-medium text-text">Neuer Meilenstein</h4>
            <button onClick={() => setShowAdd(false)} className="p-1 hover:bg-surface rounded">
              <X className="w-4 h-4" />
            </button>
          </div>
          <form onSubmit={handleAdd} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium mb-1 text-text">Titel *</label>
                <input
                  type="text"
                  value={formData.titel}
                  onChange={(e) => setFormData({ ...formData, titel: e.target.value })}
                  className="w-full px-3 py-2 border border-border rounded-button bg-surface text-text"
                  required
                  placeholder="z.B. Aufmaß vor Ort"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium mb-1 text-text">Beschreibung</label>
                <textarea
                  value={formData.beschreibung}
                  onChange={(e) => setFormData({ ...formData, beschreibung: e.target.value })}
                  className="w-full px-3 py-2 border border-border rounded-button bg-surface text-text"
                  rows={2}
                  placeholder="Optional"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1 text-text">Fällig am *</label>
                <input
                  type="date"
                  value={formData.faelligAm}
                  onChange={(e) => setFormData({ ...formData, faelligAm: e.target.value })}
                  className="w-full px-3 py-2 border border-border rounded-button bg-surface text-text"
                  required
                />
              </div>
            </div>
            <div className="flex gap-2">
              <button type="submit" className="px-4 py-2 bg-primary text-white rounded-button hover:bg-primary-dark transition">
                Erstellen
              </button>
              <button type="button" onClick={() => setShowAdd(false)} className="px-4 py-2 border border-border rounded-button hover:bg-surface-elevated transition">
                Abbrechen
              </button>
            </div>
          </form>
        </div>
      )}

      {meilensteine.length === 0 ? (
        <p className="text-text-muted text-center py-8">Keine Meilensteine definiert</p>
      ) : (
        <div className="space-y-2">
          {meilensteine.map(m => {
            const config = STATUS_CONFIG[m.status] || STATUS_CONFIG.OFFEN;
            const StatusIcon = config.icon;
            const isOverdue = m.status !== 'ERLEDIGT' && new Date(m.faelligAm) < new Date();

            return (
              <div
                key={m.id}
                className={`flex items-center justify-between p-4 bg-surface-elevated rounded-button border ${isOverdue ? 'border-red-300' : 'border-transparent'}`}
              >
                <div className="flex items-center gap-4">
                  <StatusIcon className={`w-6 h-6 ${config.color}`} />
                  <div>
                    <p className="font-medium text-text">{m.titel}</p>
                    <p className="text-sm text-text-muted">
                      Fällig: {new Date(m.faelligAm).toLocaleDateString('de-DE')}
                      {isOverdue && <span className="text-red-500 ml-2 font-medium">Überfällig!</span>}
                      {m.erledigtAm && (
                        <span className="text-green-600 ml-2">
                          Erledigt am {new Date(m.erledigtAm).toLocaleDateString('de-DE')}
                        </span>
                      )}
                    </p>
                    {m.beschreibung && (
                      <p className="text-sm text-text-muted mt-1">{m.beschreibung}</p>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <select
                    value={m.status}
                    onChange={(e) => handleStatusChange(m, e.target.value)}
                    className={`px-3 py-1 border-0 rounded text-sm font-medium cursor-pointer ${config.bgColor} ${config.color}`}
                  >
                    {Object.entries(STATUS_CONFIG).map(([key, { label }]) => (
                      <option key={key} value={key}>{label}</option>
                    ))}
                  </select>
                  <button
                    onClick={() => handleDelete(m.id)}
                    className="p-2 text-red-500 hover:bg-red-50 rounded transition"
                    title="Löschen"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
